///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Public XML checker. Mounted twice: on the public route /verificare-xml-intrastat and
//? as a panel inside the logged in app, so there is one implementation of it.
//?
//? Tiers:
//?   visitor            verdict + errors grouped by type + the first few in full
//?   active subscriber  every error, plus automatic repair and download
//?
//? The gate is real, not visual: findings beyond the free preview are never rendered, so
//? there is nothing to reveal with the developer tools. Blurring real findings would be
//? theatre anyway - the file is parsed in the browser and it is the visitor's own data.
//? For the same reason nothing is uploaded anywhere, which is worth saying out loud to
//? accountants handling other companies' figures.
//-----------------------------------------------------------------------------------------

import React, { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faFileCode,
    faCircleCheck,
    faTriangleExclamation,
    faWandMagicSparkles,
    faDownload,
    faLock,
    faSpinner,
    faShieldHalved,
    faRotateLeft,
    faPenToSquare,
    faFileExcel,
} from '@fortawesome/free-solid-svg-icons'

import { useAuth } from '../context/AuthContext'
import { createNotification } from '../services/NotificationService'
import { isStillActive } from '../services/SubscriptionsService'
import { validateIntrastatXml } from '../services/XmlValidationService'
import {
    repairIntrastatXml,
    setXmlItemValue,
    PICKABLE_FIELDS,
    NUMERIC_FIELDS,
} from '../services/XmlRepairService'
import { downloadXmlFomString } from '../services/XMLService'
import ModalSetValue from './MainAppComponents/ModalSetValue'

//? How many findings a visitor sees in full before the sign up prompt
const FREE_DETAIL_LIMIT = 3

const IntrastatValidator = ({ embedded = false }) => {
    const navigate = useNavigate()
    const { currentUser, userData } = useAuth()

    const [fileName, setFileName] = useState('')
    const [rawXml, setRawXml] = useState('')
    const [result, setResult] = useState(null)
    const [repairs, setRepairs] = useState(null)
    const [repairedXml, setRepairedXml] = useState('')
    const [busy, setBusy] = useState(false)
    //? which finding the value picker is open for: { row, field, type, value }
    const [picker, setPicker] = useState(null)
    const inputFile = useRef()

    //-------------------------------------------------------------------------------------
    //? Full access needs an account AND a live subscription - the same rule the rest of
    //? the app applies before letting anyone generate.
    //-------------------------------------------------------------------------------------
    const sub = userData?.subscriptions?.[0]
    const hasFullAccess = Boolean(
        currentUser && sub && isStillActive(sub.date, sub.duration, sub.manualDisable)
    )

    const reset = () => {
        setResult(null)
        setRepairs(null)
        setRepairedXml('')
        setFileName('')
        setRawXml('')
    }

    const readFile = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(file, 'UTF-8')
    })

    const handleFile = async (files) => {
        if (!files || files.length < 1) return
        const file = files[0]
        if (!/\.xml$/i.test(file.name)) {
            return createNotification('error', 'Selectați un fișier XML de declarație Intrastat.')
        }
        setBusy(true)
        setRepairs(null)
        setRepairedXml('')
        try {
            const text = await readFile(file)
            setFileName(file.name)
            setRawXml(text)
            setResult(validateIntrastatXml(text))
        } catch {
            createNotification('error', 'Fișierul nu a putut fi citit.')
        }
        setBusy(false)
    }

    const handleAutoFix = () => {
        if (!hasFullAccess) {
            createNotification(
                'info',
                'Corectarea automată necesită un cont cu abonament activ. Vă redirecționăm către înregistrare.'
            )
            return navigate(currentUser ? '/' : '/register')
        }
        setBusy(true)
        const { xml, repairs: applied, result: after } = repairIntrastatXml(rawXml)
        setRepairs(applied)
        setRepairedXml(xml)
        setResult(after)
        setBusy(false)
        createNotification(
            applied.length > 0 ? 'success' : 'info',
            applied.length > 0
                ? `${applied.length} ${applied.length === 1 ? 'valoare corectată' : 'valori corectate'}.`
                : 'Nu există corecții automate posibile pentru acest fișier.'
        )
    }

    const handleDownload = () => {
        const xml = repairedXml || rawXml
        if (!xml) return
        downloadXmlFomString(xml, fileName.replace(/\.xml$/i, '') + '_corectat')
    }

    //-------------------------------------------------------------------------------------
    //? Applies one manually chosen value and revalidates, so the list shrinks as the user
    //? works through it. The corrected document becomes the new working copy.
    //-------------------------------------------------------------------------------------
    const applyManualFix = (row, field, value) => {
        const source = repairedXml || rawXml
        const next = setXmlItemValue(source, row, field, value)
        if (next === source) {
            return createNotification('warning', 'Valoarea nu a putut fi aplicată în fișier.')
        }
        setRepairedXml(next)
        setResult(validateIntrastatXml(next))
        setRepairs((prev) => ([
            ...(prev || []),
            { row, label: field, message: `setat manual la "${value}"` },
        ]))
        createNotification('success', `Linia ${row}: valoarea a fost actualizată.`)
    }

    const openPicker = (finding) => {
        if (!hasFullAccess) {
            createNotification('info', 'Corectarea necesită un cont cu abonament activ.')
            return navigate(currentUser ? '/' : '/register')
        }
        const config = PICKABLE_FIELDS[finding.field]
        if (!config) return
        setPicker({ row: finding.row, field: finding.field, type: config.type, value: '' })
    }

    //-------------------------------------------------------------------------------------
    //? Renders the control that resolves a finding, when it is resolvable at all.
    //-------------------------------------------------------------------------------------
    const renderFixControl = (finding) => {
        if (!finding.field || finding.row === 'antet' || finding.row === '-') return null

        if (PICKABLE_FIELDS[finding.field]) {
            return (
                <button className='vx-fix' onClick={() => openPicker(finding)}>
                    <FontAwesomeIcon icon={hasFullAccess ? faPenToSquare : faLock} />{' '}
                    {PICKABLE_FIELDS[finding.field].label}
                </button>
            )
        }
        if (NUMERIC_FIELDS.has(finding.field)) {
            if (!hasFullAccess) {
                return (
                    <button className='vx-fix' onClick={() => openPicker({ ...finding, field: 'Cn8Code' })}>
                        <FontAwesomeIcon icon={faLock} /> Corectează
                    </button>
                )
            }
            return (
                <form
                    className='vx-fix-inline'
                    onSubmit={(e) => {
                        e.preventDefault()
                        const value = e.target.elements.v.value.trim()
                        if (value === '' || !(parseFloat(value) > 0)) {
                            return createNotification('error', 'Introduceți un număr mai mare decât 0.')
                        }
                        applyManualFix(finding.row, finding.field, String(Math.round(parseFloat(value))))
                        e.target.reset()
                    }}
                >
                    <input name='v' type='number' min='1' step='1' placeholder='valoare' />
                    <button className='vx-fix' type='submit'>
                        <FontAwesomeIcon icon={faPenToSquare} /> Aplică
                    </button>
                </form>
            )
        }
        return null
    }

    //-------------------------------------------------------------------------------------
    //? Findings collapsed by message type, so a visitor sees the full extent of the
    //? problem even while the individual lines are gated.
    //-------------------------------------------------------------------------------------
    const groupFindings = (findings) => {
        const groups = {}
        findings.forEach((f) => {
            const key = f.label || 'Altele'
            groups[key] = (groups[key] || 0) + 1
        })
        return Object.entries(groups).sort((a, b) => b[1] - a[1])
    }

    const renderVerdict = () => {
        const errors = result.findings || []

        if (result.cnVersionMismatch) {
            return (
                <div className='vx-verdict vx-verdict--warn'>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <div>
                        <b>Declarația folosește nomenclatorul {result.cnVersion}, iar aplicația verifică {result.appCnVersion}.</b>
                        <p>
                            Codurile NC8 nu pot fi verificate față de un alt an. Restul verificărilor
                            (structură, valori, țări, natura tranzacției) au fost aplicate normal.
                        </p>
                    </div>
                </div>
            )
        }
        if (errors.length === 0) {
            return (
                <div className='vx-verdict vx-verdict--ok'>
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <div>
                        <b>Fișier valid.</b>
                        <p>
                            Declarația respectă schema oficială INS și nomenclatoarele {result.appCnVersion}.
                            Poate fi încărcată în aplicația INTRASTAT.
                        </p>
                    </div>
                </div>
            )
        }
        return (
            <div className='vx-verdict vx-verdict--err'>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <div>
                    <b>{errors.length} {errors.length === 1 ? 'problemă găsită' : 'probleme găsite'}.</b>
                    <p>Aplicația oficială INTRASTAT ar respinge acest fișier în forma actuală.</p>
                </div>
            </div>
        )
    }

    const renderFindings = () => {
        const errors = result.findings || []
        if (errors.length === 0) return null

        const shown = hasFullAccess ? errors : errors.slice(0, FREE_DETAIL_LIMIT)
        const hidden = errors.length - shown.length

        return (
            <>
                <div className='vx-groups'>
                    {groupFindings(errors).map(([label, count]) => (
                        <span className='vx-chip' key={label}>{label} <b>×{count}</b></span>
                    ))}
                </div>

                <div className='popup-error-wrapper vx-list'>
                    {shown.map((f, i) => (
                        <div className='popup-error-row' key={`f-${i}`}>
                            <div>
                                {f.row !== undefined && <><b>Linia {f.row}</b> — </>}
                                <b>{f.label}</b>: {f.message}
                            </div>
                            {renderFixControl(f)}
                        </div>
                    ))}
                </div>

                {hidden > 0 && (
                    <div className='vx-gate'>
                        <div className='vx-gate__skeleton' aria-hidden='true'>
                            {[...Array(Math.min(hidden, 4))].map((_, i) => (
                                <span key={i} style={{ width: `${88 - i * 13}%` }} />
                            ))}
                        </div>
                        <div className='vx-gate__cta'>
                            <FontAwesomeIcon icon={faLock} />
                            <span>
                                <b>Încă {hidden} {hidden === 1 ? 'problemă' : 'probleme'}</b> în acest fișier.
                                Creați un cont pentru a le vedea pe toate și pentru corectare automată.
                            </span>
                            <Link className='md-btn md-btn--primary' to='/register'>Creează cont gratuit</Link>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={`vx${embedded ? ' vx--embedded' : ''}`}>
            {!embedded && (
                <header className='vx-head'>
                    <h1>Verificare fișier XML Intrastat</h1>
                    <p>
                        Verificați gratuit dacă declarația Intrastat va fi acceptată de aplicația
                        oficială INS, înainte de a o depune. Fără cont, fără încărcare pe server.
                    </p>
                </header>
            )}

            <div className='vx-card'>
                <input
                    type='file'
                    accept='.xml,text/xml,application/xml'
                    ref={inputFile}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFile(e.target.files)}
                />

                {!result ? (
                    <>
                        <div className='upload-here vx-drop' onClick={() => inputFile.current.click()}>
                            <FontAwesomeIcon className='upload-icon' icon={faFileCode} />
                        </div>
                        <p className='vx-hint'>
                            Selectați fișierul <b>.xml</b> generat pentru INTRASTAT
                        </p>
                        <p className='vx-privacy'>
                            <FontAwesomeIcon icon={faShieldHalved} /> Fișierul este verificat direct în
                            browser și <b>nu părăsește calculatorul dumneavoastră</b>.
                        </p>
                    </>
                ) : (
                    <>
                        <div className='vx-filebar'>
                            <span className='vx-filebar__name'>
                                <FontAwesomeIcon icon={faFileCode} /> {fileName}
                            </span>
                            <span className='vx-filebar__meta'>
                                {result.type === 'achizitii' ? 'Achiziții' : 'Livrări'}
                                {result.refPeriod ? ` · ${result.refPeriod}` : ''}
                                {result.itemCount ? ` · ${result.itemCount} linii` : ''}
                            </span>
                            <button className='md-btn md-btn--ghost' onClick={reset}>
                                <FontAwesomeIcon icon={faRotateLeft} /> Alt fișier
                            </button>
                        </div>

                        {busy ? <FontAwesomeIcon className='spinner' icon={faSpinner} /> : (
                            <>
                                {renderVerdict()}
                                {renderFindings()}

                                {repairs && repairs.length > 0 && (
                                    <div className='vx-repairs'>
                                        <b>{repairs.length} corecții aplicate</b>
                                        <div className='popup-error-wrapper vx-list'>
                                            {repairs.map((r, i) => (
                                                <div className='popup-error-row popup-error-row--warning' key={`r-${i}`}>
                                                    <b>Linia {r.row}</b> — <b>{r.label}</b>: {r.message}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className='vx-actions'>
                                    {(result.findings || []).length > 0 && (
                                        <button
                                            className={`md-btn md-btn--primary${hasFullAccess ? '' : ' md-btn--blocked'}`}
                                            onClick={handleAutoFix}
                                            title={hasFullAccess ? 'Corectează automat' : 'Necesită cont cu abonament activ'}
                                        >
                                            <FontAwesomeIcon icon={hasFullAccess ? faWandMagicSparkles : faLock} />{' '}
                                            Corectează automat
                                        </button>
                                    )}
                                    {repairedXml !== '' && (
                                        <button className='md-btn md-btn--primary' onClick={handleDownload}>
                                            <FontAwesomeIcon icon={faDownload} /> Descarcă fișierul corectat
                                        </button>
                                    )}
                                </div>

                                {/* The real fix is not having the errors at all */}
                                <div className='vx-upsell'>
                                    <FontAwesomeIcon icon={faFileExcel} />
                                    <div>
                                        <b>Evitați complet aceste erori.</b>
                                        <p>
                                            Generând declarația din Excel cu Program Intrastat, codurile,
                                            valorile și nomenclatoarele sunt verificate înainte de a se
                                            crea fișierul — nu mai aveți ce corecta după.
                                        </p>
                                    </div>
                                    {currentUser
                                        ? <Link className='md-btn md-btn--primary' to='/'>Generează din Excel</Link>
                                        : <Link className='md-btn md-btn--primary' to='/register'>Încearcă gratuit</Link>}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {picker && (
                <ModalSetValue
                    values={{ value: picker.value, bigIndex: picker.row, smallIndex: picker.field, type: picker.type }}
                    close={() => setPicker(null)}
                    setValue={(val, row, field) => {
                        applyManualFix(row, field, val)
                        setPicker(null)
                    }}
                />
            )}

            {!embedded && (
                <section className='vx-seo'>
                    <h2>Ce verifică acest instrument</h2>
                    <ul>
                        <li>Structura față de schema oficială INS (<code>InsSchema</code>, SchemaVersion 1.0)</li>
                        <li>Codurile NC8 — 8 cifre și existența în Nomenclatorul Combinat {result?.appCnVersion || ''}</li>
                        <li>Valorile obligatorii: valoare fiscală, greutate netă, cantitate în unități suplimentare</li>
                        <li>Țara de origine, de expediere și de destinație</li>
                        <li>Natura tranzacției, condiția de livrare, modul de transport</li>
                        <li>Codul TVA al partenerului la livrări intracomunitare</li>
                        <li>Adresa declarantului terț — cauza frecventă a erorii la import</li>
                    </ul>

                    <h2>De ce aplicația INTRASTAT respinge fișierul</h2>
                    <p>
                        Cele mai frecvente cauze sunt codurile NC8 scrise cu spații sau puncte, zeroul
                        din față pierdut de Excel, valorile zero acolo unde schema cere un număr mai
                        mare decât zero, natura tranzacției fără cele două niveluri și elementele de
                        nomenclator prezente dar goale în adresa declarantului terț — acestea din urmă
                        opresc importul cu o eroare Java, fără niciun mesaj util.
                    </p>
                    <p>
                        Dacă aveți nevoie și de generarea declarației din Excel,{' '}
                        <Link to='/register'>creați un cont gratuit</Link> sau consultați{' '}
                        <Link to='/docs'>documentația pas cu pas</Link>.
                    </p>
                </section>
            )}
        </div>
    )
}

export default IntrastatValidator
